// AdminISBNManagement.jsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  Filter,
  BookOpen,
  User,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  FileText,
  Eye,
  Mail,
  Save,
  X,
  Shield,
  TrendingUp,
  Package,
  Download,
  Copy,
  Send,
  Edit,
  Tag,
  Printer
} from 'lucide-react';
import { isbnService } from '../services/api';
import { useAuth } from '../context/AuthContext';

// Status Badge Component
const StatusBadge = ({ status }) => {
  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800 border border-yellow-200',
    approved: 'bg-green-100 text-green-800 border border-green-200',
    rejected: 'bg-red-100 text-red-800 border border-red-200',
    completed: 'bg-emerald-100 text-emerald-800 border border-emerald-200',
  };
  
  const statusIcons = {
    pending: Clock,
    approved: CheckCircle,
    rejected: XCircle,
    completed: CheckCircle,
  };
  
  const Icon = statusIcons[status] || Clock;
  const colorClass = statusColors[status] || statusColors.pending;
  
  return (
    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium ${colorClass}`}>
      <Icon className="h-3 w-3 mr-1.5" />
      {status.toUpperCase()}
    </span>
  );
};

// Payment Status Badge
const PaymentBadge = ({ status }) => {
  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800 border border-yellow-200',
    successful: 'bg-green-100 text-green-800 border border-green-200',
    failed: 'bg-red-100 text-red-800 border border-red-200',
  };
  
  const statusIcons = {
    pending: Clock,
    successful: CheckCircle,
    failed: XCircle,
  };
  
  const Icon = statusIcons[status] || Clock;
  const colorClass = statusColors[status] || statusColors.pending;
  
  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colorClass}`}>
      <Icon className="h-2.5 w-2.5 mr-1" />
      {status.toUpperCase()}
    </span>
  );
};

// ISBN Request Card Component
const ISBNRequestCard = ({ request, onViewDetails, onAssign, user }) => {
  const isAssignedToMe = request.assigned_to === user?.id;
  const canAssign = !request.assigned_to;
  const canManage = isAssignedToMe || user?.is_superuser;
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-all duration-200 group">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className={`h-12 w-12 rounded-lg flex items-center justify-center ${
              request.status === 'approved' ? 'bg-green-100' : 'bg-indigo-100'
            }`}>
              {request.status === 'approved' ? (
                <CheckCircle className="h-6 w-6 text-green-600" />
              ) : (
                <FileText className="h-6 w-6 text-indigo-600" />
              )}
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-1 group-hover:text-indigo-600 transition-colors">
              {request.book_title}
            </h3>
            <div className="flex items-center space-x-2 text-xs text-gray-600 mb-2">
              <User className="h-3 w-3" />
              <span>{request.author_name}</span>
              <span className="text-gray-400">•</span>
              <BookOpen className="h-3 w-3" />
              <span>SVP: {request.book_svp_id}</span>
            </div>
            <div className="flex items-center space-x-2">
              <StatusBadge status={request.status} />
              {request.payment_status && <PaymentBadge status={request.payment_status} />}
            </div>
          </div>
        </div>
        
        <div className="text-right">
          {request.is_paid ? (
            <>
              <div className="text-lg font-bold text-green-600 mb-1">
                GHS {request.payment_amount}
              </div>
              <div className="text-xs text-gray-500">Paid</div>
            </>
          ) : (
            <>
              <div className="text-lg font-bold text-gray-900 mb-1">GHS 650</div>
              <div className="text-xs text-gray-500">Pending Payment</div>
            </>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <div className="text-xs text-gray-500 mb-1">Studio Name</div>
          <div className="text-sm font-medium text-gray-900 truncate">
            {request.studio_name}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500 mb-1">Requested</div>
          <div className="text-sm font-medium text-gray-900">
            {new Date(request.created_at).toLocaleDateString()}
          </div>
        </div>
      </div>
      
      {request.isbn_number && (
        <div className="mb-4">
          <div className="text-xs text-gray-500 mb-1">ISBN Number</div>
          <div className="text-sm font-mono font-bold text-indigo-600 bg-indigo-50 p-2 rounded-lg">
            {request.isbn_number}
          </div>
        </div>
      )}
      
      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-500">
          {request.assigned_to ? (
            <div className="flex items-center">
              <span className="text-gray-600 font-medium">Assigned to: </span>
              <span className="ml-1">{request.assigned_admin_name}</span>
            </div>
          ) : (
            <span className="text-yellow-600 font-medium">Unassigned</span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          {canAssign && (
            <button
              onClick={() => onAssign(request.id)}
              className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Assign to Me
            </button>
          )}
          <button
            onClick={() => onViewDetails(request)}
            className="text-xs bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-200 transition-colors"
          >
            {canManage ? 'Manage' : 'View'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ISBN Details Modal Component
const ISBNDetailsModal = ({ request, isOpen, onClose, onUpdate, user }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    status: request?.status || '',
    admin_notes: request?.admin_notes || '',
    isbn_number: request?.isbn_number || '',
    assigned_to: request?.assigned_to || user?.id,
  });
  
  if (!isOpen || !request) return null;
  
  const isAssignedToMe = request.assigned_to === user?.id;
  const canManage = isAssignedToMe || user?.is_superuser;
  const canApprove = canManage && request.status === 'pending' && request.is_paid;
  const canIssueISBN = canManage && request.status === 'approved' && !request.isbn_number;
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    await onUpdate(request.id, formData);
    setIsEditing(false);
  };
  
  const handleCopyISBN = () => {
    if (request.isbn_number) {
      navigator.clipboard.writeText(request.isbn_number);
      alert('ISBN copied to clipboard!');
    }
  };
  
  const handleGenerateISBN = () => {
    // In a real implementation, this would generate a proper ISBN-13
    const generateISBN13 = () => {
      const prefix = '978';
      const group = '0';
      const publisher = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
      const title = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
      
      const isbnWithoutCheck = `${prefix}${group}${publisher}${title}`;
      let sum = 0;
      for (let i = 0; i < isbnWithoutCheck.length; i++) {
        const digit = parseInt(isbnWithoutCheck[i]);
        sum += digit * (i % 2 === 0 ? 1 : 3);
      }
      const checkDigit = (10 - (sum % 10)) % 10;
      
      return `${prefix}-${group}-${publisher}-${title}-${checkDigit}`;
    };
    
    setFormData({
      ...formData,
      isbn_number: generateISBN13()
    });
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${
              request.status === 'approved' ? 'bg-green-100' : 'bg-indigo-100'
            }`}>
              {request.status === 'approved' ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : (
                <FileText className="h-5 w-5 text-indigo-600" />
              )}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{request.book_title}</h2>
              <p className="text-sm text-gray-600">by {request.author_name} • SVP: {request.book_svp_id}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {/* Modal Content */}
        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {/* Request Information */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Author Contact</label>
                <div className="space-y-2">
                  <div className="flex items-center text-sm">
                    <User className="h-3 w-3 mr-2 text-gray-400" />
                    {request.author_name}
                  </div>
                  <div className="flex items-center text-sm">
                    <Mail className="h-3 w-3 mr-2 text-gray-400" />
                    {request.author_email}
                  </div>
                  {request.author_momo && (
                    <div className="flex items-center text-sm">
                      <DollarSign className="h-3 w-3 mr-2 text-gray-400" />
                      {request.author_momo}
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Studio Information</label>
                <div className="text-sm">
                  <div className="font-medium text-gray-900 mb-1">{request.studio_name}</div>
                  {request.reason && (
                    <div className="text-gray-600 mt-2">
                      <span className="text-gray-500">Reason: </span>
                      {request.reason}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Payment Status</label>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <PaymentBadge status={request.payment_status} />
                    <div className="text-sm font-bold text-gray-900">
                      GHS {request.payment_amount || '650.00'}
                    </div>
                  </div>
                  {request.payment_reference && (
                    <div className="text-xs text-gray-600">
                      Reference: {request.payment_reference}
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Book Status</label>
                <div className="text-sm">
                  <div className="mb-2">
                    <StatusBadge status={request.status} />
                  </div>
                  {request.issued_at && (
                    <div className="text-gray-600">
                      Issued: {new Date(request.issued_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Book Cover */}
          {request.book_cover && (
            <div className="mb-6">
              <label className="block text-xs font-medium text-gray-500 mb-1">Book Cover</label>
              <div className="w-32 h-48 bg-gray-200 rounded-lg overflow-hidden">
                <img
                  src={request.book_cover}
                  alt={request.book_title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = '/book-placeholder.png';
                  }}
                />
              </div>
            </div>
          )}
          
          {/* Admin Management Section */}
          {canManage && (
            <div className="border-t border-gray-200 pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900">Admin Management</h3>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setIsEditing(!isEditing)}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    <Edit className="h-4 w-4 inline mr-1" />
                    {isEditing ? 'Cancel Edit' : 'Edit Details'}
                  </button>
                </div>
              </div>
              
              {/* Edit Form or Display */}
              {isEditing ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({...formData, status: e.target.value})}
                        className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2"
                      >
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Assigned To</label>
                      <select
                        value={formData.assigned_to || ''}
                        onChange={(e) => setFormData({...formData, assigned_to: e.target.value})}
                        className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2"
                      >
                        <option value="">Unassigned</option>
                        {user?.is_superuser && (
                          // In production, this would be populated with actual admin users
                          <option value={user.id}>Me ({user.username})</option>
                        )}
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">ISBN Number</label>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={formData.isbn_number}
                        onChange={(e) => setFormData({...formData, isbn_number: e.target.value})}
                        className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2 font-mono"
                        placeholder="e.g., 978-0-000-00000-0"
                      />
                      <button
                        type="button"
                        onClick={handleGenerateISBN}
                        className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
                      >
                        Generate
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Format: 978-0-XXXX-XXXXX-X (ISBN-13)
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Admin Notes</label>
                    <textarea
                      value={formData.admin_notes}
                      onChange={(e) => setFormData({...formData, admin_notes: e.target.value})}
                      rows="3"
                      className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2"
                      placeholder="Add notes for the team..."
                    />
                  </div>
                  
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setIsEditing(false)}
                      className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                    >
                      <Save className="h-3 w-3 inline mr-1" />
                      Save Changes
                    </button>
                  </div>
                </form>
              ) : (
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Status: </span>
                    <StatusBadge status={request.status} />
                  </div>
                  
                  {request.isbn_number && (
                    <div className="col-span-2">
                      <span className="text-gray-500">ISBN Number: </span>
                      <div className="flex items-center space-x-2 mt-1">
                        <code className="text-sm font-mono font-bold text-indigo-600 bg-indigo-50 px-3 py-2 rounded-lg">
                          {request.isbn_number}
                        </code>
                        <button
                          onClick={handleCopyISBN}
                          className="text-sm text-indigo-600 hover:text-indigo-700"
                        >
                          <Copy className="h-3 w-3 inline mr-1" />
                          Copy
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {request.admin_notes && (
                    <div className="col-span-2">
                      <span className="text-gray-500">Admin Notes: </span>
                      <p className="text-gray-700 bg-gray-50 p-3 rounded-lg mt-1">{request.admin_notes}</p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Quick Actions */}
              {!isEditing && (
                <div className="border-t border-gray-200 pt-6 mt-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Quick Actions</h4>
                  <div className="flex flex-wrap gap-2">
                    {canApprove && (
                      <button
                        onClick={() => onUpdate(request.id, { status: 'approved' })}
                        className="px-4 py-2 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700 flex items-center"
                      >
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Approve Request
                      </button>
                    )}
                    
                    {canIssueISBN && (
                      <button
                        onClick={handleGenerateISBN}
                        className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 flex items-center"
                      >
                        <Tag className="h-3 w-3 mr-1" />
                        Issue ISBN
                      </button>
                    )}
                    
                    <button
                      onClick={() => window.open(`/book/${request.book}`, '_blank')}
                      className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 flex items-center"
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      View Book
                    </button>
                    
                    <button
                      onClick={() => window.open(`mailto:${request.author_email}`, '_blank')}
                      className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 flex items-center"
                    >
                      <Mail className="h-3 w-3 mr-1" />
                      Email Author
                    </button>
                    
                    <button
                      className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 flex items-center"
                    >
                      <Printer className="h-3 w-3 mr-1" />
                      Print Certificate
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Component
export default function AdminISBNManagement({ initialFilters = {} }) {
  const { user } = useAuth();
  const [filters, setFilters] = useState({
    status: '',
    assignedToMe: false,
    ...initialFilters
  });
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const queryClient = useQueryClient();
  
  const isSuperAdmin = user?.is_superuser || (user?.user_type === 'admin' && user?.is_staff);

  // Fetch ISBN requests
  const { data: isbnRequests = [], isLoading } = useQuery({
    queryKey: ['admin-isbn-requests', filters],
    queryFn: async () => {
      const response = await fetch(`/api/admin/isbn/requests/?status=${filters.status}&assigned_to_me=${filters.assignedToMe}`);
      if (!response.ok) throw new Error('Failed to fetch ISBN requests');
      return response.json();
    },
  });

  // Fetch statistics
  const { data: stats = {} } = useQuery({
    queryKey: ['isbn-stats'],
    queryFn: async () => {
      const response = await fetch('/api/admin/isbn/stats/');
      if (!response.ok) throw new Error('Failed to fetch statistics');
      return response.json();
    },
  });

  // Assign request mutation
  const assignMutation = useMutation({
    mutationFn: async (requestId) => {
      const response = await fetch('/api/admin/isbn/assign/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ request_id: requestId }),
      });
      if (!response.ok) throw new Error('Failed to assign request');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-isbn-requests'] });
    },
  });

  // Update request mutation
  const updateMutation = useMutation({
    mutationFn: async ({ requestId, data }) => {
      const response = await fetch('/api/admin/isbn/update/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ request_id: requestId, ...data }),
      });
      if (!response.ok) throw new Error('Failed to update request');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-isbn-requests'] });
      setShowDetailsModal(false);
    },
  });

  const handleAssign = (requestId) => {
    assignMutation.mutate(requestId);
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setShowDetailsModal(true);
  };

  const handleUpdate = (requestId, data) => {
    updateMutation.mutate({ requestId, data });
  };

  // Filter requests based on search
  const filteredRequests = isbnRequests.filter(request => {
    const matchesSearch = !searchQuery || 
      request.book_title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.author_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.studio_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (request.isbn_number && request.isbn_number.includes(searchQuery));
    
    const matchesUserRole = isSuperAdmin || 
      request.assigned_to === user?.id || 
      request.status === 'pending';
    
    return matchesSearch && matchesUserRole;
  });

  // Calculate statistics
  const revenue = stats.revenue || 0;
  const pendingCount = stats.pending || 0;
  const approvedCount = stats.approved || 0;
  const totalCount = stats.total || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">ISBN Management</h1>
          <p className="text-gray-600 font-medium">
            Manage ISBN purchase requests from authors
            {!isSuperAdmin && ' (Assigned to you only)'}
          </p>
        </div>
        <div className="flex items-center space-x-4">
          {isSuperAdmin && (
            <div className="text-right">
              <div className="text-sm text-gray-500">Total Revenue</div>
              <div className="text-2xl font-bold text-green-600">
                GHS {revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          )}
          <div className={`p-2 rounded-lg ${isSuperAdmin ? 'bg-green-100' : 'bg-blue-100'}`}>
            <Shield className={`h-5 w-5 ${isSuperAdmin ? 'text-green-600' : 'text-blue-600'}`} />
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Requests</p>
              <p className="text-2xl font-bold text-gray-900">{totalCount}</p>
            </div>
            <FileText className="h-8 w-8 text-indigo-500" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Pending Review</p>
              <p className="text-2xl font-bold text-gray-900">{pendingCount}</p>
            </div>
            <Clock className="h-8 w-8 text-yellow-500" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Approved</p>
              <p className="text-2xl font-bold text-gray-900">{approvedCount}</p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-500" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Avg. Processing</p>
              <p className="text-2xl font-bold text-gray-900">3-5 days</p>
            </div>
            <TrendingUp className="h-8 w-8 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-2xl shadow-xs border border-gray-200 overflow-hidden">
        {/* Header with Filters */}
        <div className="px-6 py-5 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search requests..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              
              <select
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
                className="text-sm border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="completed">Completed</option>
              </select>
              
              {isSuperAdmin && (
                <label className="flex items-center text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={filters.assignedToMe}
                    onChange={(e) => setFilters({...filters, assignedToMe: e.target.checked})}
                    className="mr-2 rounded border-gray-300"
                  />
                  Assigned to me
                </label>
              )}
            </div>
            
            <div className="flex items-center space-x-3">
              <button className="text-sm text-gray-600 hover:text-gray-900">
                <Filter className="h-4 w-4 inline mr-1" />
                Export
              </button>
              <button className="text-sm text-gray-600 hover:text-gray-900">
                <Download className="h-4 w-4 inline mr-1" />
                Download Report
              </button>
            </div>
          </div>
        </div>

        {/* Requests Grid */}
        <div className="p-6">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : filteredRequests.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredRequests.map(request => (
                <ISBNRequestCard
                  key={request.id}
                  request={request}
                  onViewDetails={handleViewDetails}
                  onAssign={handleAssign}
                  user={user}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                <FileText className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No ISBN requests found
              </h3>
              <p className="text-gray-600 mb-4">
                {searchQuery 
                  ? 'No requests match your search criteria.'
                  : isSuperAdmin 
                    ? 'No ISBN requests have been submitted yet.'
                    : 'No ISBN requests assigned to you.'
                }
              </p>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  Clear search
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Details Modal */}
      {selectedRequest && (
        <ISBNDetailsModal
          request={selectedRequest}
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedRequest(null);
          }}
          onUpdate={handleUpdate}
          user={user}
        />
      )}
    </div>
  );
}